import SideBarContainer from 'Containers/SideBarContainer';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import { useHistory, useParams } from 'react-router-dom';
import useEmployeeCoursesReportPage from './hooks/useEmployeeCoursesReportPage';
import { Padding } from 'Components/Padding';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import ProgressIndicator from 'Components/ProgressIndicator';
import EmployeeCoursesFilters from './components/EmployeeCoursesFilters/EmployeeCoursesFilters';
import TotalEmployeeDetails from './components/TotalEmployeeDetails/TotalEmployeeDetails';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CourseStatusChip from 'Components/CourseStatusChip';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { formatDate } from 'Utils/format-date';
import { buildTime } from 'Pages/reports/utils/buildTime';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import EmployeeCourseEvaluationsModal from './components/EmployeeCourseEvaluationsModal';
import useEmployeeCourseEvaluations from './hooks/useEmployeeCourseEvaluations';
import type { GridColDef } from '@mui/x-data-grid';

interface Params {
  employeeId: string;
}

interface Row {
  id: string;
  courseId: string;
  courseName: string;
  coursePriority: string;
  category: string;
  trainingHours: number;
  completedEvaluations: number;
  totalEvaluations: number;
  userCourseStatusId: string;
  statusName: string;
  instructorName: string | null;
  evaluationDate: string | null;
  certificationId: string | null;
}

export default function Page() {
  const { employeeId } = useParams<Params>();
  const history = useHistory();
  const {
    t2,
    employee,
    handleSearch,
    handleExport,
    coursesReportData,
    coursesReportError,
    coursesReportTotals,
    coursesReportLoading,
    pagination,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadCertificate,
  } = useEmployeeCoursesReportPage(employeeId);

  const {
    open: evaluationsModalOpen,
    selectedCourse,
    evaluations,
    loading: evaluationsLoading,
    error: evaluationsError,
    openModal: openEvaluationsModal,
    closeModal: closeEvaluationsModal,
  } = useEmployeeCourseEvaluations(employeeId);

  const buttons = [
    { icon: <EmployeesReportIcon />, tooltip: t2('employees_report_tooltip'), route: '/reporte/empleados' },
    { icon: <CoursesReportIcon />, tooltip: t2('courses_report_tooltip'), route: '/reporte/cursos' },
  ];

  const columns: GridColDef[] = [
    { field: 'courseName', headerName: t2('course_name'), flex: 1.5 },
    { field: 'coursePriority', headerName: t2('course_priority'), flex: 1 },
    { field: 'category', headerName: t2('category'), flex: 1 },
    {
      field: 'trainingHours',
      headerName: t2('training_hours'),
      flex: 1,
      renderCell: params => buildTime(params.row.trainingHours, t2),
    },
    {
      field: 'evaluationsProgress',
      headerName: t2('evaluations_progress'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <ProgressIndicator
          completed={params.row.completedEvaluations}
          total={params.row.totalEvaluations}
          emptyMessage={t2('nothing_to_evaluate')}
        />
      ),
    },
    {
      field: 'courseStatus',
      headerName: t2('course_status'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <CourseStatusChip statusId={params.row.userCourseStatusId} status={params.row.statusName} />
      ),
    },
    {
      field: 'instructor',
      headerName: t2('instructor'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: params =>
        params.row.instructorName ? params.row.instructorName : <GenericTableText text={t2('no_instructor')} />,
    },
    {
      field: 'evaluationDate',
      headerName: t2('evaluation_date'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params =>
        params.row.evaluationDate ? (
          formatDate(params.row.evaluationDate)
        ) : (
          <GenericTableText text={t2('not_evaluation_date')} tooltip={t2('not_evaluation_date_tooltip')} />
        ),
    },
    {
      field: 'downloadCertificate',
      headerName: t2('download_certificate'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: params => (
        <CertificateDownload
          certificationId={params.row.certificationId}
          onDownload={() =>
            handleDownloadCertificate(params.row.courseId, employeeId, params.row.certificationId ?? '')
          }
        />
      ),
    },
    {
      field: 'evaluationDetails',
      headerName: t2('evaluation_details'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: params => (
        <Tooltip title={t2('evaluation_details_tooltip')}>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              openEvaluationsModal(params.row.courseId, params.row.courseName);
            }}
          >
            <ListAltOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows: Row[] = coursesReportData.map(userCourse => ({
    id: userCourse.id,
    courseId: userCourse.courseId,
    courseName: userCourse.courseName,
    coursePriority: userCourse.priorityName,
    category: userCourse.categoryName,
    trainingHours: userCourse.trainingHours,
    completedEvaluations: userCourse.completedEvaluations,
    totalEvaluations: userCourse.totalEvaluations,
    userCourseStatusId: userCourse.userCourseStatusId,
    statusName: userCourse.statusName,
    instructorName: userCourse.instructorName ?? null,
    evaluationDate: userCourse.evaluationDate,
    certificationId: userCourse.certificationId,
  }));

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('employees_report'), path: '/reporte/empleados' },
            { label: t2('employee_detail'), path: `/reporte/empleados/${employeeId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${employee?.fullName}"`} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalEmployeeDetails totals={coursesReportTotals} />
        <EmployeeCoursesFilters onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={coursesReportLoading}
          error={coursesReportError}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => history.push(`/reporte/cursos/${row.courseId}`)}
        />
      </Padding>
      <EmployeeCourseEvaluationsModal
        open={evaluationsModalOpen}
        onClose={closeEvaluationsModal}
        courseName={selectedCourse?.courseName ?? ''}
        evaluations={evaluations}
        loading={evaluationsLoading}
        error={evaluationsError}
      />
    </SideBarContainer>
  );
}
